import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';
import { rateLimiter, checkLoginAttempt, recordLoginSuccess, recordLoginFailure } from '@/features/auth/utils/rateLimiter';
import { sessionManager, startSession, endSession } from '@/features/auth/utils/sessionManager';
import { showNotification } from '@mantine/notifications';
import { IconShieldX, IconLock } from '@tabler/icons-react';
import { LogoutModal } from '../components/LogoutModal';
// import { AuthContextBoundary } from '../components/AuthContextBoundary';
import { authErrorReporting } from '../services/AuthErrorReportingService';
import { authApi, tokenManager } from '../api';

interface Staff {
  id: number;
  email: string;
  name: string;
  name_kana?: string;
  role: string;
  store_id: number;
  store?: {
    id: number;
    name: string;
    code: string;
  };
  permissions?: string[];
  photo_path?: string;
  photo_url?: string;
  gender?: 'male' | 'female' | 'other';
  last_login_at?: string;
}

interface AuthContextType {
  user: Staff | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  nfcLogin: (token: string, staff: Staff) => void;
  logout: () => Promise<void>;
  // 🛡️ セキュリティ強化機能
  extendSession: () => void;
  getSessionInfo: () => {
    isActive: boolean;
    sessionDuration: number;
    timeUntilTimeout: number;
    timeUntilWarning: number;
    lastActivity: Date;
    sessionStart: Date;
  };
  getRateLimitStatus: () => {
    loginAttempts: Record<string, import('@/features/auth/utils/rateLimiter').AttemptRecord>;
    nfcAttempts: Record<string, import('@/features/auth/utils/rateLimiter').AttemptRecord>;
    generalRequests: Record<string, import('@/features/auth/utils/rateLimiter').AttemptRecord>;
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

axios.defaults.baseURL = '/api';
axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // CSRFトークンの設定
  const setupCsrf = async () => {
    await axios.get('/sanctum/csrf-cookie', { baseURL: '' });
  };

  // 初期認証チェック
  useEffect(() => {
    checkAuth();
  }, []);

  // 🛡️ ユーザー変更時のセッション管理（重複防止強化版）
  useEffect(() => {
    if (user) {
      // ログイン成功時：既存セッションをチェックしてから開始
      const sessionInfo = sessionManager.getSessionInfo();
      if (!sessionInfo.isActive) {
        startSession(logout);
      }
    } else {
      // ログアウト時：セッション管理終了
      endSession();
    }

    return () => {
      // クリーンアップ（ユーザーがいない場合のみ）
      if (!user) {
        endSession();
      }
    };
  }, [user]);

  const checkAuth = async () => {
    try {
      const token = tokenManager.getToken();
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const userData = await authApi.getCurrentUser();
        setUser(userData as Staff);
        
        // 🛡️ セッション管理はuseEffect内で自動的に開始される（重複防止のため削除）
        // const sessionInfo = sessionManager.getSessionInfo();
        // if (!sessionInfo.isActive) {
        //   startSession(logout);
        // }
      }
    } catch (error) {
      // Report auth check error
      const authError = error instanceof Error ? error : new Error('Auth check failed');
      authErrorReporting.reportError(authError, 'AuthContext', 'check_auth', {
        severity: 'low',
      });
      
      tokenManager.removeToken();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    // 🛡️ レート制限チェック
    const rateLimitCheck = checkLoginAttempt(email);
    
    if (!rateLimitCheck.allowed) {
      const errorMessage = rateLimitCheck.lockoutUntil 
        ? `ログイン試行回数を超過しました。${new Date(rateLimitCheck.lockoutUntil).toLocaleTimeString()}まで待機してください`
        : 'ログイン試行回数を超過しました。しばらく待ってからやり直してください';
      
      showNotification({
        title: 'アクセス制限',
        message: errorMessage,
        color: 'red',
        icon: <IconLock size={16} />,
        autoClose: 8000,
      });
      
      throw new Error(errorMessage);
    }

    try {
      await setupCsrf();
      const authResponse = await authApi.login({ email, password });

      if (authResponse.success && authResponse.data) {
        const { token, user: staff } = authResponse.data;
        
        // 🛡️ ログイン成功の記録
        recordLoginSuccess(email);
        
        tokenManager.setToken(token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(staff as Staff);

        // 成功通知
        showNotification({
          title: 'ログイン成功',
          message: `${staff.name}さん、ようこそ`,
          color: 'green',
          autoClose: 3000,
        });
      } else {
        // 🛡️ ログイン失敗の記録
        recordLoginFailure(email);
        throw new Error(authResponse.message || 'ログインに失敗しました');
      }
    } catch (error: unknown) {
      // 🛡️ ログイン失敗の記録
      recordLoginFailure(email);
      
      // Report error to monitoring service
      const loginError = error instanceof Error ? error : new Error('Unknown login error');
      authErrorReporting.reportError(loginError, 'AuthContext', 'login_attempt', {
        severity: 'medium',
        retryCount: 0,
      });
      
      // セキュリティを考慮したエラーメッセージ（型安全）
      let errorMessage = 'ログインに失敗しました';
      
      // Type-safe error handling
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status: number; data?: { message?: string } } };
        const status = axiosError.response?.status;
        
        if (status === 401) {
          errorMessage = 'メールアドレスまたはパスワードが正しくありません';
        } else if (status === 429) {
          errorMessage = 'ログイン試行回数が多すぎます。しばらく待ってから再試行してください';
        } else if (status === 422) {
          errorMessage = '入力内容に問題があります';
        } else if (status && status >= 500) {
          errorMessage = 'サーバーエラーが発生しました。しばらく待ってから再試行してください';
        }
      }

      showNotification({
        title: 'ログインエラー',
        message: errorMessage,
        color: 'red',
        icon: <IconShieldX size={16} />,
        autoClose: 5000,
      });

      throw new Error(errorMessage);
    }
  };

  const nfcLogin = (token: string, staff: Staff) => {
    // 🛡️ NFCログイン成功の記録（NFCカードUIDが必要だが、ここではスタッフIDで代用）
    rateLimiter.recordSuccess(staff.id.toString(), 'nfc');
    
    tokenManager.setToken(token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(staff);
    
    // セッション管理開始はuseEffect内で自動実行される
  };

  const logout = async () => {
    // ログアウトモーダルを表示
    setIsLoggingOut(true);
    
    try {
      // 🛡️ セッション管理停止
      endSession();
      
      // サーバーサイドのログアウト（V2 APIを使用）
      try {
        await authApi.logout();
      } catch (e) {
        // サーバーエラーは無視（401も含む）
        console.warn('Server logout failed, proceeding with client logout:', e);
      }

      // モーダル表示時間を待ってから状態をクリアして遷移
      setTimeout(() => {
        // ローカルの状態をクリア
        setUser(null);
        tokenManager.removeToken();
        delete axios.defaults.headers.common['Authorization'];

        // ログイン画面へ遷移
        window.location.replace('/login');
      }, 1500);
    } catch (error) {
      // エラーが発生しても強制的にログイン画面へ
      console.error('Logout error:', error);
      endSession();

      // エラー時もモーダル表示時間を待ってから遷移
      setTimeout(() => {
        tokenManager.removeToken();
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);

        window.location.replace('/login');
      }, 1500);
    }
  };

  // 🛡️ セッション延長
  const extendSession = () => {
    sessionManager.extendSession();
  };

  // 🛡️ セッション情報取得
  const getSessionInfo = () => {
    return sessionManager.getSessionInfo();
  };

  // 🛡️ レート制限状況取得（管理者用）
  const getRateLimitStatus = () => {
    return rateLimiter.getCurrentStatus();
  };

  const handleAuthFailure = (error: Error) => {
    console.error('Authentication context failure:', error);
    
    // Report error to monitoring service
    authErrorReporting.reportError(error, 'AuthContext', 'critical_failure', {
      severity: 'critical',
    });
    
    // Force logout and cleanup
    setUser(null);
    setLoading(false);
    tokenManager.removeToken();
    delete axios.defaults.headers.common['Authorization'];
    endSession();
  };

  return (
    // <AuthContextBoundary onAuthFailure={handleAuthFailure}>
      <AuthContext.Provider value={{ 
        user, 
        loading, 
        login, 
        nfcLogin, 
        logout,
        extendSession,
        getSessionInfo,
        getRateLimitStatus,
      }}>
        {children}
        <LogoutModal opened={isLoggingOut} />
      </AuthContext.Provider>
    // </AuthContextBoundary>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};